import { useEffect, useState, useRef, useCallback } from "react";
import { X, Maximize2, Minimize2 } from "lucide-react";
import { api } from "../lib/api";
import { eventBus } from "../lib/eventBus";
import { formatTime } from "../lib/format";
import type { Agent, DashboardEvent, WSMessage } from "../lib/types";

interface TerminalPanelProps {
  agent: Agent;
  onClose: () => void;
}

export function TerminalPanel({ agent, onClose }: TerminalPanelProps) {
  const [events, setEvents] = useState<DashboardEvent[]>([]);
  const [expanded, setExpanded] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  const load = useCallback(async () => {
    try {
      const res = await api.events.list({ session_id: agent.session_id, limit: 100 });
      setEvents(res.events.reverse());
    } catch {
      // silently fail
    }
  }, [agent.session_id]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    return eventBus.subscribe((msg: WSMessage) => {
      if (msg.type === "new_event") {
        const event = msg.data as DashboardEvent;
        if (event.session_id === agent.session_id) {
          setEvents((prev) => [...prev, event]);
        }
      }
    });
  }, [agent.session_id]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [events]);

  function eventColor(type: string): string {
    switch (type) {
      case "PreToolUse":
        return "text-cyan-400";
      case "PostToolUse":
        return "text-emerald-400";
      case "Stop":
        return "text-yellow-400";
      case "SessionEnd":
        return "text-red-400";
      case "Compaction":
        return "text-violet-400";
      default:
        return "text-gray-400";
    }
  }

  return (
    <div
      className={`bg-surface-1 border-t border-border flex flex-col transition-all duration-200 ${
        expanded ? "h-[60vh]" : "h-64"
      }`}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-border bg-surface-2">
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-gray-300">TERMINAL</span>
          <span className="text-[11px] text-gray-500">{agent.name}</span>
          <span className="text-[10px] font-mono text-gray-600">
            {agent.session_id.slice(0, 8)}
          </span>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setExpanded(!expanded)}
            className="p-1 text-gray-500 hover:text-gray-300 transition-colors"
          >
            {expanded ? <Minimize2 className="w-3.5 h-3.5" /> : <Maximize2 className="w-3.5 h-3.5" />}
          </button>
          <button
            onClick={onClose}
            className="p-1 text-gray-500 hover:text-red-400 transition-colors"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Terminal output */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 font-mono text-xs space-y-0.5">
        {events.length === 0 ? (
          <div className="text-gray-600 text-center py-8">
            Esperando eventos del agente...
          </div>
        ) : (
          events.map((event, i) => (
            <div key={event.id ?? i} className="flex gap-2 leading-relaxed">
              <span className="text-gray-600 flex-shrink-0">
                {event.created_at ? formatTime(event.created_at) : "--:--"}
              </span>
              <span className={`flex-shrink-0 ${eventColor(event.event_type)}`}>
                {event.event_type === "PreToolUse"
                  ? ">"
                  : event.event_type === "PostToolUse"
                    ? "<"
                    : event.event_type === "Stop"
                      ? "="
                      : "#"}
              </span>
              {event.tool_name && (
                <span className="text-accent flex-shrink-0">[{event.tool_name}]</span>
              )}
              <span className="text-gray-300 break-all">
                {event.summary || event.event_type}
              </span>
            </div>
          ))
        )}
        <div className="text-emerald-500 animate-pulse-dot inline-block">_</div>
      </div>
    </div>
  );
}
